import { BACKEND_URL } from '../config';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, Upload, Cloud, MessageCircle, Phone, Mic, Volume2, Loader } from 'lucide-react';

const PremiumFeatures = ({ user, token, language, onLogout, onToggleLanguage }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('soil');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  
  // Soil Analysis
  const [soilFile, setSoilFile] = useState(null);
  const [soilPreview, setSoilPreview] = useState(null);
  
  // Weather
  const [weather, setWeather] = useState(null);
  const [loadingWeather, setLoadingWeather] = useState(false);
  
  // Chatbot
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [listening, setListening] = useState(false);
  
  // Expert Contact
  const [expertContact, setExpertContact] = useState(null);

  const translations = {
    en: {
      soil: 'Soil Analysis',
      weather: 'Weather & Alerts',
      chatbot: 'AI Assistant',
      expert: 'Expert Advice',
      uploadSoil: 'Upload Soil Report',
      analyze: 'Analyze',
      analyzing: 'Analyzing...',
      askQuestion: 'Ask a question...',
      send: 'Send',
      callExpert: 'Call Expert',
      speakNow: 'Speak now...',
      getWeather: 'Get Weather Forecast'
    },
    hi: {
      soil: 'मिट्टी विश्लेषण',
      weather: 'मौसम और चेतावनी',
      chatbot: 'AI सहायक',
      expert: 'विशेषज्ञ सलाह',
      uploadSoil: 'मिट्टी रिपोर्ट अपलोड करें',
      analyze: 'विश्लेषण करें',
      analyzing: 'विश्लेषण हो रहा है...',
      askQuestion: 'सवाल पूछें...',
      send: 'भेजें',
      callExpert: 'विशेषज्ञ को कॉल करें',
      speakNow: 'बोलें...',
      getWeather: 'मौसम पूर्वानुमान प्राप्त करें'
    }
  };

  const t = translations[language];

  // Check if user has premium access
  useEffect(() => {
    if (user?.tier !== 'paid') {
      navigate('/payment');
    }
  }, [user, navigate]);

  // Handle soil file upload
  const handleSoilFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSoilFile(file);
      setSoilPreview(URL.createObjectURL(file));
      setResult(null);
    }
  };

  // Analyze soil
  const analyzeSoil = async () => {
    if (!soilFile) {
      setError('Please upload a soil report image');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', soilFile);

      const response = await fetch(`${BACKEND_URL}/api/premium/soil-analysis`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
      } else {
        setError(data.detail || 'Analysis failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get weather forecast
  const getWeather = async () => {
    setLoadingWeather(true);
    setError('');

    // Get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const response = await fetch(`${BACKEND_URL}/api/premium/weather`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              })
            });

            const data = await response.json();
            if (response.ok) {
              setWeather(data);
            } else {
              setError(data.detail || 'Failed to get weather');
            }
          } catch (err) {
            setError('Network error');
          } finally {
            setLoadingWeather(false);
          }
        },
        () => {
          setError('Please enable location access');
          setLoadingWeather(false);
        }
      );
    } else {
      setError('Geolocation not supported');
      setLoadingWeather(false);
    }
  };

  // Send chat message
  const sendChatMessage = async () => {
    if (!chatMessage.trim()) return;

    const userMsg = chatMessage;
    setChatMessage('');
    setChatHistory([...chatHistory, { type: 'user', message: userMsg }]);
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/premium/chatbot`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: userMsg,
          language: language
        })
      });

      const data = await response.json();

      if (response.ok) {
        setChatHistory(prev => [...prev, { type: 'bot', message: data.response }]);
        
        // Speak response
        if ('speechSynthesis' in window) {
          const utterance = new SpeechSynthesisUtterance(data.response);
          utterance.lang = language === 'hi' ? 'hi-IN' : 'en-US';
          window.speechSynthesis.speak(utterance);
        }
      } else {
        setError(data.detail || 'Failed to get response');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  // Voice input
  const startVoiceInput = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.lang = language === 'hi' ? 'hi-IN' : 'en-US';
      recognition.continuous = false;

      recognition.onstart = () => {
        setListening(true);
      };

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setChatMessage(transcript);
        setListening(false);
      };

      recognition.onerror = () => {
        setListening(false);
        setError('Voice recognition failed');
      };

      recognition.onend = () => {
        setListening(false);
      };

      recognition.start();
    } else {
      setError('Voice recognition not supported');
    }
  };

  // Get expert contact
  useEffect(() => {
    const fetchExpertContact = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/premium/expert-contact`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setExpertContact(data);
        }
      } catch (err) {
        console.error('Error fetching expert contact:', err);
      }
    };

    if (activeTab === 'expert') {
      fetchExpertContact();
    }
  }, [activeTab, token]);

  return (
    <div className="premium-page">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="logo">
            <Leaf size={32} />
            <span>BoAI Premium</span>
          </div>
          <div className="header-actions">
            <div className="tier-badge paid" data-testid="premium-badge">PREMIUM</div>
            <button className="lang-toggle" onClick={onToggleLanguage}>
              {language.toUpperCase()}
            </button>
            <button className="icon-btn" onClick={() => navigate('/dashboard')}>
              Dashboard
            </button>
            <button className="icon-btn" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="premium-content">
        <div className="container">
          {/* Premium Tabs */}
          <div className="premium-tabs">
            <button
              className={activeTab === 'soil' ? 'active' : ''}
              onClick={() => setActiveTab('soil')}
              data-testid="soil-tab"
            >
              <Upload size={20} />
              {t.soil}
            </button>
            <button
              className={activeTab === 'weather' ? 'active' : ''}
              onClick={() => setActiveTab('weather')}
              data-testid="weather-tab"
            >
              <Cloud size={20} />
              {t.weather}
            </button>
            <button
              className={activeTab === 'chatbot' ? 'active' : ''}
              onClick={() => setActiveTab('chatbot')}
              data-testid="chatbot-tab"
            >
              <MessageCircle size={20} />
              {t.chatbot}
            </button>
            <button
              className={activeTab === 'expert' ? 'active' : ''}
              onClick={() => setActiveTab('expert')}
              data-testid="expert-tab"
            >
              <Phone size={20} />
              {t.expert}
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          {/* Soil Analysis Tab */}
          {activeTab === 'soil' && (
            <div className="premium-tab-content">
              <div className="card">
                <h2>{t.soil}</h2>
                <div className="upload-area">
                  {soilPreview ? (
                    <img src={soilPreview} alt="Soil report" className="preview-image" />
                  ) : (
                    <div className="upload-placeholder">
                      <Upload size={48} />
                      <p>{t.uploadSoil}</p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSoilFileChange}
                    id="soil-file"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="soil-file" className="btn-secondary">
                    {soilPreview ? 'Change Image' : t.uploadSoil}
                  </label>
                </div>

                <button
                  className="btn-primary"
                  onClick={analyzeSoil}
                  disabled={!soilFile || loading}
                >
                  {loading ? t.analyzing : t.analyze}
                </button>

                {result && (
                  <div className="soil-results">
                    <div className="result-section">
                      <h3>NPK Analysis</h3>
                      <p>{language === 'en' ? result.npk_analysis_en : result.npk_analysis_hi}</p>
                    </div>
                    <div className="result-section">
                      <h3>pH Level</h3>
                      <p>{language === 'en' ? result.ph_level_en : result.ph_level_hi}</p>
                    </div>
                    <div className="result-section">
                      <h3>Soil Type</h3>
                      <p>{language === 'en' ? result.soil_type_en : result.soil_type_hi}</p>
                    </div>
                    <div className="result-section">
                      <h3>Fertilizer Recommendations</h3>
                      <p>{language === 'en' ? result.fertilizer_recommendations_en : result.fertilizer_recommendations_hi}</p>
                    </div>
                    <div className="result-section">
                      <h3>Suitable Crops</h3>
                      <p>{language === 'en' ? result.suitable_crops_en : result.suitable_crops_hi}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Weather Tab */}
          {activeTab === 'weather' && (
            <div className="premium-tab-content">
              <div className="card">
                <h2>{t.weather}</h2>
                <button className="btn-primary" onClick={getWeather} disabled={loadingWeather}>
                  {loadingWeather ? <Loader className="spin" /> : t.getWeather}
                </button>

                {weather && (
                  <div className="weather-content">
                    <div className="current-weather">
                      <h3>Current Weather</h3>
                      <p className="temp">{weather.current.temp}°C</p>
                      <p>{weather.current.condition}</p>
                      <p>Humidity: {weather.current.humidity}%</p>
                    </div>

                    <div className="forecast">
                      <h3>7-Day Forecast</h3>
                      <div className="forecast-grid">
                        {weather.forecast_7day.map((day, index) => (
                          <div key={index} className="forecast-day">
                            <p className="day-name">{day.day}</p>
                            <p className="condition">{day.condition}</p>
                            <p className="temp">{day.temp_max}° / {day.temp_min}°</p>
                            <p className="rain">Rain: {day.rain_probability}%</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="pest-alerts">
                      <h3>Pest Alerts</h3>
                      <div className={`alert-badge ${weather.pest_alerts.risk_level.toLowerCase()}`}>
                        {weather.pest_alerts.risk_level} Risk
                      </div>
                      <p>{language === 'en' ? weather.pest_alerts.alerts_en : weather.pest_alerts.alerts_hi}</p>
                    </div>

                    {weather.note && (
                      <p className="weather-note">{weather.note}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Chatbot Tab */}
          {activeTab === 'chatbot' && (
            <div className="premium-tab-content">
              <div className="card chatbot-card">
                <h2>{t.chatbot}</h2>
                
                <div className="chat-container">
                  <div className="chat-messages">
                    {chatHistory.map((msg, index) => (
                      <div key={index} className={`chat-message ${msg.type}`}>
                        <p>{msg.message}</p>
                      </div>
                    ))}
                    {loading && (
                      <div className="chat-message bot">
                        <Loader className="spin" size={16} />
                      </div>
                    )}
                  </div>

                  <div className="chat-input-container">
                    <button
                      className={`voice-btn ${listening ? 'listening' : ''}`}
                      onClick={startVoiceInput}
                      disabled={loading || listening}
                    >
                      {listening ? <Volume2 size={20} /> : <Mic size={20} />}
                    </button>
                    <input
                      type="text"
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                      placeholder={listening ? t.speakNow : t.askQuestion}
                      disabled={loading || listening}
                    />
                    <button
                      className="btn-primary"
                      onClick={sendChatMessage}
                      disabled={!chatMessage.trim() || loading}
                    >
                      {t.send}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Expert Advice Tab */}
          {activeTab === 'expert' && (
            <div className="premium-tab-content">
              <div className="card expert-card">
                <h2>{t.expert}</h2>
                {expertContact && (
                  <div className="expert-content">
                    <Phone size={64} className="expert-icon" />
                    <h3>Agricultural Expert</h3>
                    <p>{language === 'en' ? expertContact.message_en : expertContact.message_hi}</p>
                    <a href={`tel:${expertContact.phone}`} className="expert-phone">
                      {expertContact.phone}
                    </a>
                    <p className="expert-hours">{expertContact.available}</p>
                    <button className="btn-primary" onClick={() => window.location.href = `tel:${expertContact.phone}`}>
                      <Phone size={20} />
                      {t.callExpert}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PremiumFeatures;
